if (!customElements.get('marame-lookbook')) {
  customElements.define('marame-lookbook', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.track = this.querySelector('.marame-lookbook__track');
      this.pages = [...this.querySelectorAll('[data-lookbook-page]')];
      if (!this.track || !this.pages.length) return;
      this.book = this.querySelector('.marame-lookbook__book');
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.previous = this.querySelector('[data-lookbook-prev]');
      this.next = this.querySelector('[data-lookbook-next]');
      this.status = this.querySelector('[data-lookbook-status]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.wide = matchMedia('(min-width: 750px)');
      this.index = this.targetIndex = this.transitionId = 0;
      this.previous?.addEventListener('click', () => this.turn(-1), options);
      this.next?.addEventListener('click', () => this.turn(1), options);
      this.track.addEventListener('keydown', event => {
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        if (event.target.closest('input, select, textarea, button')) return;
        const directions = { ArrowRight: 1, ArrowLeft: -1, Home: -Infinity, End: Infinity };
        if (event.key === 'Escape') { this.cancelTurn(); this.update(); return; }
        if (!(event.key in directions)) return;
        event.preventDefault();
        this.turn(directions[event.key], true);
      }, options);
      this.track.addEventListener('pointerdown', event => {
        if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
        this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, horizontal: false };
      }, { ...options, passive: true });
      this.track.addEventListener('pointermove', event => {
        const start = this.pointer;
        if (!start || start.id !== event.pointerId) return;
        const dx = start.x - event.clientX, dy = start.y - event.clientY;
        if (!start.horizontal && Math.abs(dy) > 8 && Math.abs(dy) >= Math.abs(dx)) { this.pointer = null; return; }
        if (Math.abs(dx) >= 8 && Math.abs(dx) > Math.abs(dy) * 1.4) {
          start.horizontal = true;
          this.track.setPointerCapture?.(event.pointerId);
          event.preventDefault();
        }
      }, options);
      this.track.addEventListener('pointerup', event => {
        const start = this.pointer;
        this.pointer = null;
        if (!start || start.id !== event.pointerId) return;
        const dx = start.x - event.clientX, dy = start.y - event.clientY;
        if (Math.abs(dx) < 8 || Math.abs(dx) <= Math.abs(dy) * 1.4) return;
        this.suppressClickUntil = performance.now() + 400;
        this.turn(dx > 0 ? 1 : -1);
      }, options);
      this.track.addEventListener('pointercancel', () => { this.pointer = null; }, options);
      this.track.addEventListener('click', event => {
        if (performance.now() < (this.suppressClickUntil || 0)) { event.preventDefault(); event.stopPropagation(); }
      }, { ...options, capture: true });
      this.track.addEventListener('dragstart', event => event.preventDefault(), options);
      this.wide.addEventListener('change', () => this.resize(), options);
      this.motion.addEventListener?.('change', () => { if (this.motion.matches) { this.cancelTurn(); this.update(); } }, options);
      this.layoutWidth = this.track.clientWidth;
      this.layoutPages = this.perPage;
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(this.track);
      document.addEventListener('shopify:block:select', event => {
        const page = this.pages.find(item => item.dataset.blockId === event.detail?.blockId);
        if (page) this.go(this.pages.indexOf(page), false);
      }, options);
      this.querySelector('.marame-lookbook__controls').hidden = false;
      this.setAttribute('data-enhanced', '');
      this.update();
      this.warm();
    }
    get perPage() { return this.wide.matches ? 2 : 1; }
    get lastIndex() { return Math.floor((this.pages.length - 1) / this.perPage) * this.perPage; }
    pageWidth() { return this.track.clientWidth / this.perPage; }
    turn(direction, keyboard = false) {
      let target = this.targetIndex + direction * this.perPage;
      if (direction === -Infinity) target = 0;
      if (direction === Infinity) target = this.lastIndex;
      return this.go(target, !this.motion.matches, keyboard);
    }
    async ready(index) {
      await Promise.all(this.pages.slice(index, index + this.perPage).map(async page => {
        const image = page.querySelector('img');
        if (!image) return;
        image.loading = 'eager';
        if (image.complete && image.naturalWidth > 0) return;
        if (image.complete && image.naturalWidth === 0) {
          const retrySource = image.currentSrc || image.getAttribute('src');
          if (retrySource) image.src = retrySource;
        }
        if (image.decode) await image.decode();
        else await new Promise((resolve, reject) => {
          image.addEventListener('load', resolve, { once: true });
          image.addEventListener('error', reject, { once: true });
        });
      }));
    }
    async go(index, animate = true, keyboard = false) {
      const target = Math.min(this.lastIndex, Math.max(0, Math.floor(index / this.perPage) * this.perPage));
      this.cancelTurn();
      this.targetIndex = target;
      const token = this.transitionId;
      if (target === this.index) { this.track.scrollTo({ left: target * this.pageWidth(), behavior: 'instant' }); this.update(); return; }
      this.setAttribute('aria-busy', 'true');
      try { await this.ready(target); }
      catch {
        if (token !== this.transitionId || !this.abort) return;
        this.targetIndex = this.index;
        this.removeAttribute('aria-busy');
        this.status.textContent = this.dataset.errorLabel;
        return;
      }
      if (token !== this.transitionId || !this.abort) return;
      const activePage = this.pages.find(page => page.contains(document.activeElement));
      if (keyboard || activePage && !this.pages.slice(target, target + this.perPage).includes(activePage)) this.track.focus({ preventScroll: true });
      const from = this.index;
      if (animate && !this.motion.matches) this.makeTurn(from, target);
      this.index = this.targetIndex = target;
      this.track.scrollTo({ left: target * this.pageWidth(), behavior: 'instant' });
      this.removeAttribute('aria-busy');
      this.update();
      this.warm();
    }
    pageCopy(page) {
      const copy = page?.cloneNode(true) || document.createElement('div');
      copy.inert = true;
      copy.removeAttribute('id');
      copy.setAttribute('aria-hidden', 'true');
      for (const el of [copy, ...copy.querySelectorAll('*')]) {
        el.removeAttribute('id');
        el.removeAttribute('data-shopify-editor-block');
        el.removeAttribute('data-shopify-editor-section');
      }
      return copy;
    }
    makeTurn(from, target) {
      if (!this.book?.animate && !this.pages[from]?.animate) return;
      const forward = target > from;
      const spread = this.perPage === 2;
      const layer = document.createElement('div');
      layer.className = `marame-lookbook__turn ${forward ? 'is-forward' : 'is-backward'} ${spread ? 'is-spread' : 'is-single'}`;
      layer.setAttribute('aria-hidden', 'true'); layer.inert = true;
      if (spread) {
        const fixed = document.createElement('div'); fixed.className = 'marame-lookbook__turn-static';
        fixed.append(this.pageCopy(this.pages[from + (forward ? 0 : 1)])); layer.append(fixed);
      }
      const leaf = document.createElement('div'); leaf.className = 'marame-lookbook__leaf';
      const front = document.createElement('div'); front.className = 'marame-lookbook__leaf-face is-front';
      front.append(this.pageCopy(this.pages[from + (spread && forward ? 1 : 0)]));
      const back = document.createElement('div'); back.className = 'marame-lookbook__leaf-face is-back';
      if (spread) back.append(this.pageCopy(this.pages[target + (forward ? 0 : 1)]));
      leaf.append(front, back); layer.append(leaf); this.book.append(layer);
      this.turnLayer = layer;
      this.animation = leaf.animate([
        { transform: 'rotateY(0deg)', offset: 0 },
        { transform: `rotateY(${forward ? -78 : 78}deg) skewY(${forward ? -1 : 1}deg) scaleX(.985)`, offset: spread ? .48 : .78 },
        { transform: `rotateY(${forward ? -180 : 180}deg)`, offset: 1 }
      ], { duration: 760, easing: 'cubic-bezier(.35,0,.4,1)', fill: 'forwards' });
      const animation = this.animation;
      animation.finished.then(() => {
        if (this.animation !== animation) return;
        layer.remove(); this.turnLayer = null; this.animation = null;
      }).catch(() => {});
    }
    warm() {
      const first = Math.max(0, this.index - this.index % 2);
      this.pages.slice(first, Math.max(first + 2, this.index + this.perPage * 2)).forEach(page => {
        const image = page.querySelector('img');
        if (image) { image.loading = 'eager'; image.decode?.().catch(() => {}); }
      });
    }
    update() {
      const end = Math.min(this.index + this.perPage, this.pages.length);
      const range = end > this.index + 1 ? `${this.index + 1}–${end}` : String(end);
      this.status.textContent = `${this.dataset.pageLabel} ${range} ${this.dataset.ofLabel} ${this.pages.length}`;
      this.previous.disabled = this.index === 0;
      this.next.disabled = this.index >= this.lastIndex;
      this.pages.forEach((page, i) => {
        const visible = i >= this.index && i < end;
        if (!visible && page.contains(document.activeElement)) this.track.focus({ preventScroll: true });
        page.inert = !visible;
        if (visible) page.removeAttribute('aria-hidden');
        else page.setAttribute('aria-hidden', 'true');
      });
    }
    resize() {
      if (!this.track.clientWidth) return;
      if (this.layoutWidth === this.track.clientWidth && this.layoutPages === this.perPage) return;
      this.layoutWidth = this.track.clientWidth;
      this.layoutPages = this.perPage;
      this.cancelTurn();
      this.index = this.targetIndex = Math.min(this.lastIndex, Math.floor(this.index / this.perPage) * this.perPage);
      this.track.scrollTo({ left: this.index * this.pageWidth(), behavior: 'instant' });
      this.update(); this.warm();
    }
    cancelTurn() {
      this.transitionId += 1;
      this.animation?.cancel(); this.animation = null;
      this.turnLayer?.remove(); this.turnLayer = null;
      this.targetIndex = this.index;
      this.removeAttribute('aria-busy');
    }
    disconnectedCallback() {
      this.cancelTurn();
      this.abort?.abort(); this.abort = null;
      this.resizeObserver?.disconnect();
    }
  });
}
